/* eslint-disable radix */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import AutocompleteWrapper from '../../common/Autocomplete/AutocompleteWrapper';
import { useFetchOrgUnitOptions, getClinicianOptionsForOrgUnits } from '../../../actions/areas/shared';
import globals from '../../../globals/communityGlobals';
import KeyboardDateSelector from '../../common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import { getErrorProps, checkRequiredOnBlur } from '../../../utils/keyboardSelectorFunctions';
import { extractDateOnly, isDateFromBeforeDateTo, isValidDate } from '../../../utils/dates';

const errors = globals.getArea('error');

export default function ClinicianGroupForm({ props }) {
  const auth = useSelector(state => state.global.auth);

  const clinicianGroup = props.clinicianGroup;
  const [state, setState] = useState({
    id: clinicianGroup.id ? clinicianGroup.id : null,
    name: clinicianGroup.name,
    selectedClinicians: clinicianGroup.clinicianOptions,
    selectedOrgUnit: clinicianGroup.orgUnitOption,
    validFrom: moment(clinicianGroup.validFrom).toDate(),
    validTo: moment(clinicianGroup.validTo).toDate(),
    clinicianOptions: [],
    errors: [],
  });
  const [dateValidationErrors, setDateValidationErrors] = useState({});

  const getOrgUnitClinicians = orgUnitId => {
    getClinicianOptionsForOrgUnits(auth, { teamIds: [orgUnitId] }, false, data => {
      setState(newState => ({
        ...newState,
        clinicianOptions: data.options,
      }));
    });
  };

  useEffect(() => {
    if (state.selectedOrgUnit) {
      getOrgUnitClinicians(state.selectedOrgUnit.id);
    }
  }, []);

  const { data: orgUnitOptions, isDataLoading: isOrgUnitsLoading } = useFetchOrgUnitOptions(false);

  if (isOrgUnitsLoading) return null;

  const headerTitle = state.id ? `Edit clinician group` : `Create clinician group`;

  const validateFormData = () => {
    const errorMsg = [];

    if (!state.name || state.name.trim() === '') {
      errorMsg.push({ name: 'name', message: 'Name cannot be empty.' });
    }

    if (state.name && state.name.length > 100) {
      errorMsg.push({ name: 'name', message: 'Name cannot be more than 100 characters.' });
    }

    if (!state.selectedOrgUnit) {
      errorMsg.push({ name: 'selectedOrgUnit', message: 'Org Unit must be selected.' });
    }

    if (state.selectedClinicians.length < 2 || state.selectedClinicians.length > 8) {
      errorMsg.push({
        name: 'selectedClinicians',
        message: 'The number of selected Clinicians must be between 2 and 8.',
      });
    }

    if (!state.validFrom || !state.validTo) {
      errorMsg.push({ name: 'validFromvalidTo', message: 'Date From and Date To cannot be empty.' });
    }

    if (!isDateFromBeforeDateTo(state.validFrom, state.validTo, true)) {
      errorMsg.push({ name: 'validFromvalidTo', message: props.events.locale.getMsg('dateToBeforeDateFrom') });
    }

    if (
      (state.validFrom && state.validFrom < extractDateOnly(new Date(globals.now))) ||
      (state.validTo && state.validTo < extractDateOnly(new Date(globals.now)))
    ) {
      errorMsg.push({ name: 'validFromvalidTo', message: 'Date From and Date To cannot be in the past.' });
    }

    setState({
      ...state,
      errors: errorMsg,
    });

    return errorMsg.length > 0;
  };

  const onSaveClick = () => {
    if (validateFormData()) return;

    const model = {
      ClinicianGroupId: state.id,
      Name: state.name.trim(),
      OrgUnitId: state.selectedOrgUnit.id,
      ClinicianIds: state.selectedClinicians.map(x => x.id),
      ValidFrom: moment(state.validFrom).format(globals.dateFormats.localApiFormat),
      ValidTo: moment(state.validTo).format(globals.dateFormats.localApiFormat),
    };

    props.events.createOrEditClinicianGroup(model);
  };

  const setSelectedValue = (key, value) => {
    setState({
      ...state,
      [key]: value,
      errors: state.errors.filter(x => !x.name.includes(key)),
    });
  };

  const onChangeDropDown = (key, values) => {
    if (key === 'selectedOrgUnit') {
      setState({
        ...state,
        [key]: values,
        clinicianOptions: [],
        selectedClinicians: [],
        errors: state.errors.filter(x => x.name !== key),
      });
      if (values) getOrgUnitClinicians(values.id);
    } else {
      setSelectedValue(key, values);
    }
  };

  const errorPlaceholder = () => {
    if (state.errors.length <= 0 && props.errorMsg === null) return null;

    return (
      <span className="form-validation-error">
        {props.errorMsg ? props.errorMsg.message.split('\n').map((it, i) => <p key={i}>{it}</p>) : null}
        {state.errors.map((item, i) => (
          <p key={`${i}_${item.name}`}>{item.message}</p>
        ))}
      </span>
    );
  };

  const handleDateChange = (fieldName, date) => {
    let errorMsg = null;

    if (!date) {
      errorMsg = errors.getMsg('requiredFieldValidationMessage');
    }

    setDateValidationErrors(prevState => ({
      ...prevState,
      [fieldName]: errorMsg,
    }));
    setState(prevState => ({ ...prevState, [fieldName]: date }));
  };

  const headerRender = () => {
    return (
      <div className="sidePanel-header form-header">
        <div className="sidePanel-header-subtitle">
          <Box display="flex">
            <Box flexGrow={1} py={1}>
              <Typography variant="subtitle1">{headerTitle}</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => props.events.onClose()}>
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  const mainContentRender = () => {
    return (
      <form className="sidePanel-content content-top-padding" noValidate autoComplete="off">
        <Typography variant="h3" gutterBottom>
          Clinician group details
        </Typography>
        {errorPlaceholder()}
        <div className="sidePanel-form">
          <TextField
            id="nameTextField"
            required
            defaultValue={state.name}
            onChange={e => setSelectedValue('name', e.target.value)}
            label={props.events.locale.getMsg('name')}
          />
          <AutocompleteWrapper
            fieldKey="selectedOrgUnit"
            data={orgUnitOptions}
            placeholder={props.events.locale.getMsg('orgUnits')}
            isMultiple={false}
            defaultValue={state.selectedOrgUnit}
            fullWidth
            onChange={onChangeDropDown}
          />
          <AutocompleteWrapper
            fieldKey="selectedClinicians"
            data={state.clinicianOptions}
            placeholder={props.events.locale.getMsg('clinicians')}
            isMultiple
            fullWidth
            limitTags={3}
            onChange={onChangeDropDown}
            defaultValue={state.selectedClinicians}
            disabled={state.clinicianOptions.length <= 0}
            isCheckbox
          />
          <Box display="flex" flexDirection="row">
            <Box p={1} flexGrow={1} style={{ width: '35%' }}>
              <KeyboardDateSelector
                required
                id="datefrom-picker-inline"
                label={props.events.locale.getMsg('dateFrom')}
                value={state.validFrom}
                onChange={date => handleDateChange('validFrom', date)}
                onBlur={event =>
                  checkRequiredOnBlur(event.target.value, setDateValidationErrors, 'validFrom', dateValidationErrors)
                }
                errorProps={getErrorProps(dateValidationErrors.validFrom)}
                minDate={new Date(globals.now)}
                minDateMessage={errors.getMsg('cannotBeInThePast')}
              />
            </Box>
            <Box p={1} flexGrow={1} style={{ width: '35%' }}>
              <KeyboardDateSelector
                required
                id="datefrom-picker-inline"
                label={props.events.locale.getMsg('dateTo')}
                value={state.validTo}
                onChange={date => handleDateChange('validTo', date)}
                onBlur={event =>
                  checkRequiredOnBlur(event.target.value, setDateValidationErrors, 'validTo', dateValidationErrors)
                }
                errorProps={getErrorProps(dateValidationErrors.validTo)}
                minDate={state.validFrom}
                minDateMessage={props.events.locale.getMsg('dateToBeforeDateFrom')}
              />
            </Box>
          </Box>
        </div>
      </form>
    );
  };

  const footerRender = () => {
    return (
      <div className="sidePanel-footer">
        <div className="sidePanel-buttons">
          <div className="sidePanel-BackButton">
            <Button
              variant="contained"
              color="secondary"
              onClick={() => props.events.onClose()}
              style={{ marginRight: '10px' }}
            >
              {props.events.locale.getMsg('back')}
            </Button>
          </div>
          <div className="sidePanel-saveButton">
            <Button
              variant="contained"
              color="primary"
              onClick={onSaveClick}
              disabled={!isValidDate(state.validFrom, true) || !isValidDate(state.validTo, true)}
            >
              {state.id ? props.events.locale.getMsg('save') : props.events.locale.getMsg('create')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer anchor="right" open onOpen={() => {}} onClose={() => {}}>
        <div className="sidePanel-wrapper" style={{ width: '500px' }}>
          {headerRender()}
          {mainContentRender()}
          {footerRender()}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
